import { AlertCircle, CheckCircle2, Download, Pen } from "lucide-react";
import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Alert, AlertDescription } from "@/components/ui/alert";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./public-flow-completion-view.i18n";

type PublicFlowCompletionViewProps = {
  clientName?: string;
  allowPdfGen: boolean;
  allowSigning: boolean;
  /**
   * QL-263 — a PDF the flow already rendered while submitting ("generate PDF
   * automatically on submit"). When set, the view opens in the ready-to-download
   * state; the generate button is only offered when it is null.
   */
  readyPdf?: { pdfUrl: string; fileName: string } | null;
  onGeneratePdf: () => Promise<{ pdfUrl: string; fileName: string } | null>;
  onSign: (signatureDataUrl: string) => Promise<{ success: boolean; error?: string }>;
  onDone: () => void;
};

export function PublicFlowCompletionView({
  clientName,
  allowPdfGen,
  allowSigning,
  readyPdf = null,
  onGeneratePdf,
  onSign,
  onDone,
}: PublicFlowCompletionViewProps) {
  const t = useT(T);

  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfGenerated, setPdfGenerated] = useState(readyPdf !== null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(readyPdf?.pdfUrl ?? null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(readyPdf?.fileName ?? null);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signatureEmpty, setSignatureEmpty] = useState(true);
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const sigCanvas = useRef<SignatureCanvas>(null);

  const handleGeneratePdf = async () => {
    setPdfGenerating(true);
    setPdfError(null);

    const result = await onGeneratePdf();

    if (!result) {
      setPdfError(t.failedToGeneratePdf);
      setPdfGenerating(false);
      return;
    }

    setPdfUrl(result.pdfUrl);
    setPdfFileName(result.fileName);
    setPdfGenerating(false);
    setPdfGenerated(true);
  };

  const handleDownloadPdf = () => {
    if (!pdfUrl) return;

    // iOS Safari ignores the <a download> attribute for cross-origin URLs (the S3
    // presigned URL), so the anchor approach does nothing useful there. Open the PDF
    // in a new tab instead, where the user can view it and save via the share sheet.
    // Other browsers keep the anchor-based download.
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !("MSStream" in window);

    if (isIOS) {
      window.open(pdfUrl, "_blank");
    } else {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = pdfFileName || "document.pdf";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    setPdfDownloaded(true);
  };

  const handleClearSignature = () => {
    sigCanvas.current?.clear();
    setSignatureEmpty(true);
  };

  const handleSign = async () => {
    if (signatureEmpty || !sigCanvas.current) {
      setSignatureError(t.pleaseProvideSignature);
      return;
    }

    setSigning(true);
    setSignatureError(null);

    const signatureDataUrl = sigCanvas.current.toDataURL("image/png");
    const result = await onSign(signatureDataUrl);

    if (!result.success) {
      setSignatureError(result.error || t.failedToSign);
      setSigning(false);
      return;
    }

    setSigning(false);
    setSigned(true);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <Card>
        <CardHeader className="text-center pb-4">
          <div className="mx-auto w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="w-10 h-10 text-success" />
          </div>
          <CardTitle className="text-3xl">{t.thankYou}</CardTitle>
          <CardDescription className="text-lg mt-2">{t.submittedSuccessfully}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="text-center space-y-2">
            {clientName && <p className="text-lg">{t.thankYouName(clientName)}</p>}
            <p className="text-muted-foreground">{t.responsesReviewed}</p>
          </div>

          {/* PDF Generation Option */}
          {allowPdfGen && (
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Download className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{t.downloadSummary}</p>
                  <p className="text-sm text-muted-foreground">
                    {pdfGenerated ? t.pdfReady : t.generatePdfDescription}
                  </p>
                </div>
              </div>

              {pdfError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{pdfError}</AlertDescription>
                </Alert>
              )}

              {!pdfGenerated ? (
                <Button onClick={handleGeneratePdf} className="w-full" loading={pdfGenerating}>
                  {t.generatePdf}
                </Button>
              ) : (
                <Button
                  onClick={handleDownloadPdf}
                  variant={pdfDownloaded ? "outline" : "default"}
                  className="w-full"
                >
                  {pdfDownloaded ? t.downloaded : t.downloadPdf}
                </Button>
              )}
            </div>
          )}

          {/* Signing Option */}
          {allowSigning && pdfGenerated && pdfDownloaded && (
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-3">
                <Pen className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{t.signDocument}</p>
                  <p className="text-sm text-muted-foreground">
                    {signed ? t.documentSigned : t.drawSignature}
                  </p>
                </div>
              </div>

              {signatureError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{signatureError}</AlertDescription>
                </Alert>
              )}

              {!signed ? (
                <>
                  <div className="border-2 border-dashed border-muted-foreground/20 rounded-lg overflow-hidden bg-white">
                    <SignatureCanvas
                      ref={sigCanvas}
                      penColor="black"
                      backgroundColor="white"
                      canvasProps={{
                        style: { width: "100%", height: "160px" },
                        className: "cursor-crosshair",
                      }}
                      onBegin={() => setSignatureEmpty(false)}
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button variant="outline" onClick={handleClearSignature} disabled={signing}>
                      {t.clear}
                    </Button>
                    <Button
                      onClick={handleSign}
                      disabled={signatureEmpty}
                      className="sm:flex-1"
                      loading={signing}
                    >
                      {t.signDocumentButton}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    {t.signatureAgreement}
                  </p>
                </>
              ) : (
                <div className="flex items-center justify-center p-4 bg-success/10 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-success mr-2" />
                  <span className="font-medium text-success">{t.signedSuccessfully}</span>
                </div>
              )}
            </div>
          )}

          {/* Done Button */}
          <div className="pt-4 border-t">
            <Button onClick={onDone} variant="outline" className="w-full">
              {t.done}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
